<template>
  <div>
    <viewer-form :can-back="true" @back="toList">
      <text-browse-item label="発行依頼ID" :value="id" />
      <text-browse-item label="ステータス" :value="status_text" class="mb-5" />
      <datetime-browse-item label="依頼日時" :value="request_at" />
      <text-browse-item label="依頼物件名・棟名" :value="building.name" />
      <text-browse-item label="依頼物件名・棟名（カナ）" :value="building.name_kana" />
      <text-browse-item label="依頼物件住戸番号" :value="building_house_number" />
      <text-browse-item label="所有者" :value="owner_name" />
      <text-browse-item label="依頼物件郵便番号" :value="building.postal_code" />
      <text-browse-item label="依頼物件住所" :value="building.address" />
      <div v-show="requiredDocumentType !== $constant.DOCUMENT_TYPE.HIDDEN">
        <file-browse-item label="依頼主確認書類" :value="required_document_path" :file-name="requiredDocumentTypeName" :is-enable="!!required_document_path" />
      </div>
      <text-browse-item label="発行書類" :value="issueDocumentName" />
      <text-browse-item label="手数料の支払方法" :value="paymentMethodName" />
      <text-browse-with-unit-item label="請求金額" :value="billing_price" unit="円" />
      <form-layout-panel label="振込明細確認">
        <div v-for="transfer_statement_file in transfer_statement_files" :key="transfer_statement_file.id">
          <file-browse-item label="" :value="transfer_statement_file.path" :is-enable="!!transfer_statement_file.path" :uploadedAt="transfer_statement_file.uploaded_at" />
        </div>
      </form-layout-panel>
      <datetime-browse-item label="振込通知日" :value="transfer_notification_date" />
      <textarea-browse-item v-if="reissue_reason" label="再発行理由" :value="reissue_reason" />
      <textarea-browse-item label="備考" :value="note" class="mb-5" />
      <text-browse-item v-if="requester.brokerage_company && !isOwner" label="依頼仲介業者" :value="requester.brokerage_company" />
      <text-browse-item label="依頼者" :value="requester.requested_by" />
      <text-browse-item v-if="!isOwner" label="依頼者住所" :value="requester.address" />
      <textarea-browse-item label="連絡先" :value="contact" />
      <licence-browse-item v-if="!isOwner" label="免許番号" :type="requester.licence_type" :number="requester.licence_number" />
      <detail-document-item label="資料" text="資料一覧を表示" :to="toPublicDocument"/>
      <danger-alert v-if="alertMessage" :message="alertMessage" />
      <template #expansion-buttons-left>
        <div ref="talk">
          <base-button v-if="isIssuanceTalkAllowed" class="btn-dark" :id="isShowTalk ? 'talk-button-actived' : 'talk-button'" :text="!isShowTalk ? 'トークを表示' : 'トークを非表示'" @click="onShowTalk" />
        </div>
      </template>
      <template #expansion-buttons-right>
        <new-window-button v-if="canDownloadReciept" text="領収書発行" :window-url="getPrintPreviewUrl()" :window-width="980" :window-height="644" @click="updateStatusHandler($constant.RECEIPT_STATUS.RECEIPT_STATUS_ISSUED)" />
        <new-window-button v-if="canRedownloadReciept" text="領収書再発行" :window-url="getPrintPreviewUrl()" :window-width="980" :window-height="644" @click="updateStatusHandler($constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_DONE)" />
        <base-button class="btn-dark" v-if="canRequestReissueReceipt" text="領収書再発行依頼" @click="updateStatusHandler($constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_REQUEST)" />
        <router-button v-if="isValidPeriod && !isStatusReissueRequest" text="発行物ダウンロードを表示" :to="toDownload" />
        <router-button v-if="isWaitingBankTransfer" text="振込明細書アップロードを表示" :to="toUploadTransferStatement" />
        <router-button v-if="canReissue" text="再発行依頼" :to="toReissue" />
        <change-status-button v-if="canCancel" text="キャンセル" class="btn-danger px-4"
                              @click="confirmOperateWithReason($constant.ISSUANCE_REQUEST_OPERATION.CANCELED)"
        />
      </template>
      <template #talk v-if="isShowTalk">
        <div id="card-talk" class="position-relative mt-3 mb-6">
          <div class="card card-container position-relative rounded-3 shadow mx-3 mx-5 p-5">
            <tab v-model="selectedTab">
              <tab-button v-for="(tab, i) in tabs" :key="`tab${i}`" :val="tab.value" :is-active="getActive(tab.value)">
                {{ tab.label }}
              </tab-button>
            </tab>
            <tab-content v-model="selectedTab">
              <div class="tab-panel">
                <ChatContentList
                  :is-bookmark="selectedTab != 'talk'"
                  :messages="selectedTab === 'talk' ? talk_messages : talk_bookmarks"
                  :issuance-request-id = "is_guest ? guest_issuance_request_id : $route.params.id"
                  ref="tabContent"
                />
              </div>
            </tab-content>
          </div>
          <div class="wrapper footer-talk shadow">
            <div style="gap: 24px;" class="talk-footer-container d-flex justify-content-center align-items-center mx-3 px-5">
              <upload-talk-file
                :issues="issues"
                :filename="talk_form?.name"
                :set-upload-file-handler="uploadTalkFile"
                @delete="() => deleteTalkFile()"
                :error-message="errors?.talk_form"
              />
              <text-talk-input
                :value="message"
                :set-handler="setTalkMessage"
                @dropFile="uploadTalkFile"
                :set-error-validate-talk-file="setErrorValidateTalkFile"
              />
              <base-button class="btn-primary" text="送信" @click="sendTalkMessage" :disabled="!talk_form && !message?.trim()?.length" />
            </div>
          </div>
        </div>
      </template>
    </viewer-form>
    <teleport to="body">
      <show-error-upload-modal ref="showErrorModal" />
      <edit-confirm-modal-with-input-area first-description-text="理由を入力して更新ボタンを押下してください。"
                                          second-description-text="入力した内容は管理会社に通知されます。"
                                          input-text-label="理由"
                                          :text-max-length="5000"
                                          :required="true"
                                          @beforeUpdate="setNotificationReason"
                                          @update="doOperation()"
                                          ref="confirmModalWithReason"
                                          default-value="不要になったため。"
      />
      <edit-confirm-modal-with-input-area first-description-text="理由を入力して更新ボタンを押下してください。"
                                          second-description-text="入力した内容は管理会社に通知されます。"
                                          input-text-label="理由" :text-max-length="5000"
                                          :required="true" @beforeUpdate="setReceiptReason" 
                                          @update="doReceiptRequest()" 
                                          ref="confirmReceiptModal"
                                          :default-value="''"
      />
    </teleport>
  </div>
</template>

<script>
import {mapState, mapMutations, mapGetters} from 'vuex';
import {convertPaymentMethodToName} from '@/constants/payment_method';
import AddonLoadPageEvent from '@/mixin/AddonLoadPageEvent';
import ViewerForm from "@/views/templates/forms/ViewerForm";
import TextBrowseItem from "@/views/molecules/browseItems/TextBrowseItem";
//import DateBrowseItem from "@/views/molecules/browseItems/DateBrowseItem";
import DatetimeBrowseItem from "@/views/molecules/browseItems/DatetimeBrowseItem";
import TextBrowseWithUnitItem from '@/views/molecules/browseItems/TextBrowseWithUnitItem';
import FileBrowseItem from "@/views/molecules/browseItems/FileBrowseItem";
import LicenceBrowseItem from "@/views/molecules/browseItems/LicenceBrowseItem";
import TextareaBrowseItem from "@/views/molecules/browseItems/TextareaBrowseItem";
import RouterButton from '@/views/molecules/buttons/RouterButton';
import FormLayoutPanel from "@/views/templates/FormLayoutPanel";
import ChangeStatusButton from '@/views/molecules/buttons/ChangeStatusButton';
import EditConfirmModalWithInputArea from "@/views/molecules/modals/EditConfirmModalWithInputArea";
import DangerAlert from "@/views/atoms/alerts/DangerAlert";
import DetailDocumentItem from "@/views/molecules/browseItems/DetailDocumentItem";
import Tab from '@/views/atoms/tab/Tab';
import TabButton from '@/views/atoms/tab/TabButton';
import TabContent from '@/views/atoms/tab/TabContent';
import ChatContentList from "@/views/molecules/ChatContentList";
import BaseButton from '@/views/atoms/BaseButton';
import UploadTalkFile from '@/views/molecules/browseItems/UploadTalkFile';
import ShowErrorUploadModal from '@/views/molecules/modals/ShowErrorUploadModal';
import TextTalkInput from '@/views/atoms/inputs/TextTalkInput';
import NewWindowButton from '@/views/molecules/buttons/NewWindowButton';
import { trimStartEndOfString } from '@/mixin/helper';

export default {
  name: 'IssuanceRequestViewer',
  mixins: [AddonLoadPageEvent],
  components: {
    ViewerForm,
    TextBrowseItem,
//    DateBrowseItem,
    DatetimeBrowseItem,
    TextBrowseWithUnitItem,
    FileBrowseItem,
    LicenceBrowseItem,
    TextareaBrowseItem,
    RouterButton,
    FormLayoutPanel,
    ChangeStatusButton,
    EditConfirmModalWithInputArea,
    DangerAlert,
    DetailDocumentItem,
    Tab,
    TabButton,
    TabContent,
    ChatContentList,
    BaseButton,
    UploadTalkFile,
    ShowErrorUploadModal,
    TextTalkInput,
    NewWindowButton,
  },
  inject: [
    '$moment',
    '$constant',
  ],
  beforeRouteLeave() {
    this.resetData();
  },
  data() {
    return {
      operation: null,
      alertMessage: null,
      isShowTalk: false,
      tabs: [
        {
          label: 'トーク',
          value: 'talk',
        },
        {
          label: 'ブックマーク一覧',
          value: 'bookmark',
        },
      ],
      selectedTab: 'talk',
    }
  },
  computed: {
    ...mapState('issuance_request_form', {
      id: state => state.data.id ? state.data.id.toString() : null,
      status_text: state => state.data.status_text,
      building_house_number: state => state.data.building_house_number,
      owner_name: state => state.data.owner_name,
      request_at: state => state.data.request_at,
      required_document_path: state => state.data.required_document_path,
      transfer_statement_files: state => state.data.transfer_statement_files,
      transfer_notification_date: state => state.data.transfer_notification_date,
      building: state => state.data.building,
      issue_document: state => state.data.issue_document,
      billing_price: state => state.data.billing_price,
      requester: state => state.data.requester,
      note: state => state.data.note,
      contact: state => state.data.contact,
      reissue_reason: state => state.data.reissue_reason,
      status: state => state.data.status,
      issue_expire_date: state => state.data.issue_expire_date,
      payment_method_code: state => state.data.payment_method,
      management_company_id: state => state.data.public_management_id ? state.data.public_management_id : null,
      errors: state => state.errors,
      talk_form: state => state.talk_form.upload_file,
      message: state => state.talk_form.message,
      talk_messages: state => state.talk.messages,
      talk_bookmarks: state => state.talk.bookmarks,
      is_issuance_talk_allowed: state => state.data.building.management_company.is_issuance_talk_allowed,
      requiredDocumentType: state => state.data.required_document_type,
      reissuePossibleType: state => state.data.building.management_company.reissue_possible_type,
      receipt_issued_status: state => state.data.receipt_issued_status,
      use_receipt: state => state.data.building.management_company.use_receipt,
    }),
    ...mapState('account', {
      is_guest: state => state.is_guest,
      guest_issuance_request_id: state => state.guest.issuance_request_id,
      guestType: state => state.guest_type,
    }),
    ...mapGetters('issuance_request_form', [
      'requiredDocumentTypeName',
      'issueDocumentName',
    ]),
    isValidPeriod() {
      // ダウンロード有効期限チェック
      return this.$moment(this.$moment().format('YYYY-MM-DD'), 'YYYY-MM-DD').isSameOrBefore(this.issue_expire_date);
    },
    isStatusReissueRequest() {
      // 再発行依頼かのチェック
      return this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code;
    },
    isWaitingBankTransfer() {
      // 振込明細アップロードできるステータスかのチェック
      return this.status === this.$constant.ISSUANCE_REQUEST_STATUS.WAITING_BANK_TRANSFER.code
          || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.TRANSFER_STATEMENT_NG.code;
    },
    paymentMethodName() {
      return convertPaymentMethodToName(this.payment_method_code);
    },
    isOwner() {
      return this.guestType === this.$constant.GUEST_TYPE.OWNER.code;
    },
    toUploadTransferStatement() {
      if (this.is_guest) {
        return {name: 'GuestIssuanceRequestUploadTransferStatement', params: {id: this.guest_issuance_request_id, showBackButton: 'true'}};
      } else {
        return {name: 'IssuanceRequestUploadTransferStatement', params: {id: this.$route.params.id, showBackButton: 'true'}};
      }
    },
    toPublicDocument() {
      return { 
        name: this.is_guest ? 'GuestPublicDocument' : 'PublicDocument',
        params: { management_company_id: this.management_company_id, type: 'Viewer', id: this.$route.params.id, showBackButton: 'true' },
      };
    },
    toDownload() {
      if (this.is_guest) {
        return {name: 'GuestIssuanceRequestDownload', params: {id: this.guest_issuance_request_id, showBackButton: 'true'}};
      } else {
        return {name: 'IssuanceRequestDownload', params: {id: this.$route.params.id, showBackButton: 'true'}};
      }
    },
    toReissue() {
      if (this.is_guest) {
        return {name: 'GuestReissuanceRequestRegister', params: {id: this.guest_issuance_request_id, showBackButton: 'true'}};
      } else {
        return {name: 'ReissuanceRequestRegister', params: {id: this.$route.params.id, showBackButton: 'true'}};
      }
    },
    canReissue() {
      // 発行完了、または、ダウンロード完了の場合のみ、ダウンロード有効期限と管理会社マスタの設定値に従う
      switch (this.status) {
        case this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code:
        case this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code:
          if (this.isValidPeriod) return false;
          return this.reissuePossibleType === this.$constant.REISSUE_POSSIBLE_TYPE.ALWAYS.code;      
        default:
          return false;
      }
    },
    canCancel() {
      return this.status == this.$constant.ISSUANCE_REQUEST_STATUS.NEW_APPLICATION.code       // 新規申請
        || this.status == this.$constant.ISSUANCE_REQUEST_STATUS.CONFIRMING_APPLICATION.code  // 申請内容確認中
        || this.status == this.$constant.ISSUANCE_REQUEST_STATUS.MODIFICATION_REQUEST.code    // 申請内容修正要求
        || this.status == this.$constant.ISSUANCE_REQUEST_STATUS.REAPPLICATION.code           // 再申請
        || (this.status == this.$constant.ISSUANCE_REQUEST_STATUS.WAITING_BANK_TRANSFER.code  // 発行手数料振込待ち
        && this.transfer_statement_files.length === 0);
    },
    isIssuanceTalkAllowed() {
      return this.is_issuance_talk_allowed == 1;
    },
    canDownloadReciept() {
      return this.use_receipt
        && this.payment_method_code === 0
        && this.billing_price !== 0
        && (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUING.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code)
        && (this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_UNISSUED);
    },
    canRedownloadReciept() {
      return this.use_receipt
        && this.payment_method_code === 0
        && this.billing_price !== 0
        && (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUING.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code)
        && (this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_PERMIT);
    },
    canRequestReissueReceipt() {
      return this.use_receipt
        && this.payment_method_code === 0
        && this.billing_price !== 0
        && (this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUING.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.ISSUANCE_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.DOWNLOAD_COMPLETED.code
            || this.status === this.$constant.ISSUANCE_REQUEST_STATUS.REISSUANCE_REQUEST.code)
        && (this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_ISSUED
            ||this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_NG
            ||this.receipt_issued_status === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_DONE);
    },
  },
  watch: {
    $route(newRoute, oldRoute) {
      if (this.$route.params.id && newRoute.name === oldRoute.name) {
        this.loadPage();
      }
    },
  },
  methods: {
    ...mapMutations('issuance_request_form', [
      'resetData',
      'resetTalkUploadFile',
      'setTalkMessage',
      'setError',
    ]),
    async loadPage() {
      if (this.is_guest) {
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
        if (this.guest_issuance_request_id === null) {
          // ゲストでまだ発行依頼が申請されていなければ、発行依頼画面に遷移
          return this.$router.replace({name: 'GuestIssuanceRequestRegister'});
        }
      }
      return this.$store.dispatch('issuance_request_form/load', {
        // ゲストの場合は認証時に発行依頼IDを取得済み
        id: this.is_guest ? this.guest_issuance_request_id : this.$route.params.id
      }).then(() => {
        this.$root.showFlashMessage();
        this.$nextTick(() => {
          if (this.$route.hash) {
            const refName = this.$route.hash.replace('#', '');
            const el = this.$refs[refName];
            if (el) el.scrollIntoView({ behavior: 'smooth'});
            const talkButton = document.querySelector('#talk-button');
            if (talkButton) talkButton.click();
          }
        });
      }).catch(() => this.$root.failApi());
    },
    loadTalkMessage() {
      return this.$store.dispatch('issuance_request_form/loadTalkMessage', {
        issuanceRequest: this.is_guest ? this.guest_issuance_request_id : this.$route.params.id,
      })
      .then(() => {
        this.$nextTick(() => {
          const talkContainer = document.querySelector('#card-talk');
          if (talkContainer) {
            window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: "smooth" });
          }
          const chatPanel = document.querySelector('#chat-panel');
          if (this.talk_messages.length && chatPanel) {
            chatPanel.scrollTop = chatPanel.scrollHeight;
          }
        })
      })
      .catch((error) => {
        this.$refs.showErrorModal.show(error);
      })
    },
    toList() {
      this.$router.push({name: 'IssuanceRequestList'});
    },
    setNotificationReason(value) {
      this.notificationReason = value;
    },
    setReceiptReason(value) {
      this.receiptReason = value;
    },
    confirmOperateWithReason(operation) {
      this.operation = operation;
      this.$refs.confirmModalWithReason.show();
    },
    async doOperation() {
      if (this.is_guest) {
        await this.$store.dispatch('account/authGuest', {
          access_key: sessionStorage.getItem('accessKey')
        }).catch((reason) => this.$root.failApi(reason));
      }
      this.$store.dispatch('issuance_request_form/operate', {
        operation: this.operation,
        notificationReason: this.notificationReason
      }).then(() => {
        if (this.is_guest) {
          this.$router.push({name: 'GuestIssuanceRequestViewer', params: {'id': this.guest_issuance_request_id}});
        } else {
          this.toList();
        }
        this.$root.showFlashMessage();
      }).catch((message) => {
        if (message) {
          this.alertMessage = message;
        } else {
          this.$root.failApi();
        }
      });
    },
    doReceiptRequest() {
      this.updateStatusHandler(this.operation, this.receiptReason);
    },
    getActive(tab) {
      return this.selectedTab === tab;
    },
    onShowTalk() {
      this.isShowTalk = !this.isShowTalk;
      if (this.isShowTalk) {
        this.loadTalkMessage().then(() => {
          this.$store.dispatch('talk_notifieds/load').catch(() => this.$root.failApi());
        });
      }
    },
    uploadTalkFile(file) {
      if (file?.name) {
        this.$store.dispatch('issuance_request_form/validateTalkFile', {
          file,
        })
        .then(() => {
          this.$store.commit('issuance_request_form/setTalkUploadFile' , { value: file });
        })
        .catch((error) => {
          this.$refs.showErrorModal.show(error);
        });
      }
    },
    sendTalkMessage(){
      document.getElementById('talk')?.click();
      const tempTalkMessage = trimStartEndOfString(this.message);
      if (tempTalkMessage.length > 5000) {
        const errorMessage = 'メッセージは、5000文字以下で指定してください。';
        this.$refs.showErrorModal.show(errorMessage);
        return;
      }
      this.$store.dispatch('issuance_request_form/sendTalkMesssage', {
        issuanceRequest: this.is_guest ? this.guest_issuance_request_id : this.$route.params.id,
      })
      .then(() => {
        const textTalkArea = document.getElementById('text-talk-input');
        textTalkArea.value = '';
        this.loadTalkMessage();
      }).catch((error) => {
        this.$refs.showErrorModal.show(error);
      })
    },
    deleteTalkFile() {
      this.resetTalkUploadFile();
    },
    setErrorValidateTalkFile(errorMessage) {
      this.$refs.showErrorModal.show(errorMessage);
    },
    getPrintPreviewUrl() {
      let resolvedRoute = this.$router.resolve({
        name: 'ReceiptIssue',
        params: {id: this.$route.params.id}
      });
      return resolvedRoute.href;
    },
    updateStatusHandler(payload, reason = null) {
      // 再発行の場合、理由をモーダルで確認
      if (payload === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_REQUEST && reason === null) {
        // operationをセット
        this.operation = payload;
        this.$refs.confirmReceiptModal.show();
      } else {
        this.$store.dispatch('issuance_request_form/updateReceiptStatus', {
          id: this.$route.params.id,
          value: payload,
          reason: reason,
        }).then(() => {
          // 領収書再発行依頼の場合のみメッセージを表示し、画面を上部にスクロール
          if (payload === this.$constant.RECEIPT_STATUS.RECEIPT_STATUS_REISSUE_REQUEST) {
            this.$store.commit('setFlashMessage', { value: '領収書再発行依頼を行いました' }, { root: true });
            this.$root.showFlashMessage();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          this.loadPage();
        });
      }
    }
  },
}
</script>
